import { useEffect } from "react";
import type { Face } from "~/lib/faces";

export const SITE_NAME = "JGson";
export const SITE_TAGLINE =
  "Software for games, mobile, AR, AI, and fintech";
export const SITE_URL = "https://jgson.net";
export const DEFAULT_DESCRIPTION =
  "JGson is the one-engineer studio of João Vidotto. Ten years of shipping across games, Unity & Unreal, native Android, React Native, AR/VR, AI, and fintech automation. Drag the cube to explore.";

interface SeoOptions {
  /** Browser tab title and og:title. Auto-suffixed with " · JGson". */
  title?: string;
  description?: string;
  /** Pathname including leading slash, e.g. "/play". Used for canonical + og:url. */
  pathname?: string;
}

/**
 * Set per-route SEO via DOM mutations on mount. Works for browser title,
 * tab description, and search engines that render JS (Google). LinkedIn /
 * Twitter / Facebook crawlers do NOT run JS — they see the prerendered
 * per-route HTML written by scripts/prerender.mjs (dist/<route>/index.html),
 * which patches the static meta block at build time. This hook is the
 * runtime override for SPA navigations after the initial page load.
 */
export function useSeo(opts: SeoOptions): void {
  const { title, description, pathname } = opts;

  useEffect(() => {
    const fullTitle = title
      ? `${title} · ${SITE_NAME}`
      : `${SITE_NAME} — ${SITE_TAGLINE}`;
    const desc = description ?? DEFAULT_DESCRIPTION;
    const url = pathname ? `${SITE_URL}${pathname}` : SITE_URL;

    document.title = fullTitle;
    setMetaName("description", desc);
    setMetaProperty("og:title", fullTitle);
    setMetaProperty("og:description", desc);
    setMetaProperty("og:url", url);
    setMetaName("twitter:title", fullTitle);
    setMetaName("twitter:description", desc);
    setLinkCanonical(url);
  }, [title, description, pathname]);
}

/** Convenience wrapper for cubie routes — derives SEO from the Face entry. */
export function useFaceSeo(face: Face): void {
  useSeo({
    title: face.label,
    description: face.blurb,
    pathname: face.route,
  });
}

function setMetaName(name: string, content: string): void {
  let el = document.head.querySelector(
    `meta[name="${name}"]`,
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaProperty(property: string, content: string): void {
  let el = document.head.querySelector(
    `meta[property="${property}"]`,
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkCanonical(href: string): void {
  let el = document.head.querySelector(
    'link[rel="canonical"]',
  ) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
